import {
  CSSProperties,
  PointerEvent as ReactPointerEvent,
  RefObject,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

type Geometry = { top: number; left: number; width: number; height: number };

export type ResizeDirection = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

type UsePanelGeometry = {
  panelRef: RefObject<HTMLElement>;
  panelStyle: CSSProperties;
  startDrag: (_event: ReactPointerEvent<HTMLElement>) => void;
  startResize: (
    direction: ResizeDirection,
  ) => (event: ReactPointerEvent<HTMLElement>) => void;
  isInteracting: boolean;
};

const MIN_WIDTH = 400;
const MIN_HEIGHT = 600;
const MARGIN = 12;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

const isElement = (target: EventTarget | null): target is Element =>
  target instanceof Element;

/**
 * The sidekick have two modes, it can be floating or docked to the right hand
 * side. When the sidekick is floating, the user can drag and resize the
 * sidekick. Note that the sidekick can not be smaller than 400 * 600 px in
 * float mode.
 */
export const usePanelGeometry = (isFloating: boolean): UsePanelGeometry => {
  const panelRef = useRef<HTMLElement>(null);
  const [geometry, setGeometry] = useState<Geometry | null>(null);
  const [isInteracting, setIsInteracting] = useState(false);

  useLayoutEffect(() => {
    if (isFloating && !geometry) {
      setGeometry({
        width: MIN_WIDTH,
        height: MIN_HEIGHT,
        left: Math.max(0, window.innerWidth - MIN_WIDTH - MARGIN),
        top: Math.max(0, window.innerHeight - MIN_HEIGHT - MARGIN),
      });
    }
  }, [isFloating, geometry]);

  const startDrag = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (isElement(event.target) && event.target.closest('button')) return;
      if (!geometry) return;
      event.preventDefault();
      const startX = event.clientX;
      const startY = event.clientY;
      const base = geometry;
      setIsInteracting(true);

      const handleMove = (moveEvent: PointerEvent) => {
        setGeometry({
          ...base,
          left: clamp(
            base.left + (moveEvent.clientX - startX),
            0,
            window.innerWidth - base.width,
          ),
          top: clamp(
            base.top + (moveEvent.clientY - startY),
            0,
            window.innerHeight - base.height,
          ),
        });
      };

      const handleUp = () => {
        setIsInteracting(false);
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', handleUp);
      };

      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleUp);
    },
    [geometry],
  );

  const startResize = useCallback(
    (direction: ResizeDirection) => (event: ReactPointerEvent<HTMLElement>) => {
      if (!geometry) return;
      event.preventDefault();
      const startX = event.clientX;
      const startY = event.clientY;
      const base = geometry;
      setIsInteracting(true);

      const handleMove = (moveEvent: PointerEvent) => {
        const dx = moveEvent.clientX - startX;
        const dy = moveEvent.clientY - startY;
        const next = { ...base };

        if (direction.includes('e')) {
          next.width = clamp(
            base.width + dx,
            MIN_WIDTH,
            window.innerWidth - base.left,
          );
        }
        if (direction.includes('s')) {
          next.height = clamp(
            base.height + dy,
            MIN_HEIGHT,
            window.innerHeight - base.top,
          );
        }
        if (direction.includes('w')) {
          const applied = clamp(dx, -base.left, base.width - MIN_WIDTH);
          next.left = base.left + applied;
          next.width = base.width - applied;
        }
        if (direction.includes('n')) {
          const applied = clamp(dy, -base.top, base.height - MIN_HEIGHT);
          next.top = base.top + applied;
          next.height = base.height - applied;
        }

        setGeometry(next);
      };

      const handleUp = () => {
        setIsInteracting(false);
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', handleUp);
      };

      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleUp);
    },
    [geometry],
  );

  const panelStyle: CSSProperties = geometry
    ? {
        top: geometry.top,
        left: geometry.left,
        width: geometry.width,
        height: geometry.height,
        right: 'auto',
        bottom: 'auto',
      }
    : {};

  return { panelRef, panelStyle, startDrag, startResize, isInteracting };
};
